import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowRight, Home, Lock, ShieldCheck, AlertTriangle, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import Sidebar from "../components/Sidebar";
import MobileTopBar from "../components/MobileTopBar";
import StudioFooter from "../components/StudioFooter";
import { useStudio } from "../context/StudioContext";
import { createOrder, verifyPayment, getConfig } from "../lib/api";
import { openRazorpay } from "../lib/razorpay";
import { parseRupees, formatINR } from "../lib/pricing";
import { saveAbandonedCheckout, readAbandonedCheckout, clearAbandonedCheckout } from "../lib/abandonedCheckout";
import { saveCustomerInfo, trackEvent } from "../lib/tracker";

export default function CheckoutPage() {
  const { email, plan, clearPlan } = useStudio();
  const navigate = useNavigate();
  const [customer, setCustomer] = useState({
    name: "",
    email: email || "",
    phone: "",
    gst: "",
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [agree, setAgree] = useState(true);
  const [resumed, setResumed] = useState(false);

  // Hydrate from abandoned checkout if present
  useEffect(() => {
    const saved = readAbandonedCheckout();
    if (saved && saved.customer) {
      setCustomer((c) => ({
        name: saved.customer.name || c.name,
        email: saved.customer.email || c.email,
        phone: saved.customer.phone || c.phone,
      }));
      setResumed(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const totals = useMemo(() => {
    const subtotal = plan.reduce((n, i) => n + parseRupees(i.price), 0);
    const gst = subtotal * 0.18;
    return { subtotal, gst, total: subtotal + gst };
  }, [plan]);

  const update = (k, v) => setCustomer((c) => ({ ...c, [k]: v }));

  const pay = async () => {
    setError("");
    if (!customer.name.trim() || !customer.email.trim() || !customer.phone.trim()) {
      setError("Please fill in your name, email and mobile.");
      return;
    }
    if (!plan.length) {
      setError("Your plan is empty.");
      return;
    }
    if (!agree) {
      setError("Please accept the terms & privacy policy to continue.");
      return;
    }

    setLoading(true);
    try {
      saveCustomerInfo({ name: customer.name.trim(), email: customer.email.trim(), phone: customer.phone.trim() });
      await trackEvent({ eventType: "checkout_start", meta: { total: totals.total, item_count: plan.length } });
      const payload = {
        customer_name: customer.name.trim(),
        customer_email: customer.email.trim(),
        customer_phone: customer.phone.trim(),
        customer_gst: (customer.gst || "").trim().toUpperCase(),
        subtotal: totals.subtotal,
        gst_amount: totals.gst,
        total_amount: totals.total,
        items: plan.map((i) => ({
          code: i.code,
          title: i.title,
          service: i.service || "",
          plan_name: i.planName || "",
          price_amount: parseRupees(i.price),
          billing: i.billing || "",
        })),
      };
      const created = await createOrder(payload);

      // If we still have a fully-placeholder key (no real Razorpay key set), simulate silently.
      const canOpenRazorpay = created.razorpay_key_id && !(created.razorpay_key_id.includes("placeholder"));
      if (!canOpenRazorpay) {
        const verify = await verifyPayment({
          order_id: created.order_id,
          razorpay_order_id: created.razorpay_order_id,
          razorpay_payment_id: "pay_sim_" + Math.random().toString(36).slice(2),
          razorpay_signature: "sim_" + Math.random().toString(36).slice(2),
        });
        if (verify.ok) {
          clearAbandonedCheckout();
          clearPlan();
          toast.success("Simulated payment (no Razorpay key configured).");
          navigate(`/order/${created.order_id}`);
          return;
        }
        throw new Error("Simulated payment failed");
      }

      const result = await openRazorpay({
        keyId: created.razorpay_key_id,
        amountPaise: created.amount_paise,
        // Only pass order_id if backend created a real Razorpay order (needs valid secret).
        razorpayOrderId: created.placeholder_mode ? undefined : created.razorpay_order_id,
        name: "AuthorOS by Zebralearn",
        description: `Studio plan (${plan.length} item${plan.length > 1 ? "s" : ""})`,
        prefill: {
          name: customer.name,
          email: customer.email,
          contact: customer.phone,
        },
      });

      if (result.success) {
        const verify = await verifyPayment({
          order_id: created.order_id,
          razorpay_order_id: result.response.razorpay_order_id,
          razorpay_payment_id: result.response.razorpay_payment_id,
          razorpay_signature: result.response.razorpay_signature,
        });
        if (verify.ok) {
          clearAbandonedCheckout();
          clearPlan();
          navigate(`/order/${created.order_id}`);
          return;
        }
        setError("Payment could not be verified. Please try again.");
        saveAbandonedCheckout({
          customer,
          orderId: created.order_id,
          razorpayOrderId: created.razorpay_order_id,
          plan,
          total: totals.total,
        });
      } else if (result.dismissed) {
        setError("Payment cancelled. Your cart is safe \u2014 tap Retry to try again.");
        saveAbandonedCheckout({
          customer,
          orderId: created.order_id,
          razorpayOrderId: created.razorpay_order_id,
          plan,
          total: totals.total,
        });
        toast.message("Checkout saved. You can resume anytime.");
      } else {
        const desc = result.error && result.error.description ? result.error.description : "Payment failed. Please try again.";
        setError(desc);
        saveAbandonedCheckout({
          customer,
          orderId: created.order_id,
          razorpayOrderId: created.razorpay_order_id,
          plan,
          total: totals.total,
        });
      }
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || "Something went wrong. Please retry.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen bg-brand-ink">
      <Sidebar />
      <main className="flex-1 overflow-x-hidden">
        <MobileTopBar title="Checkout" />
        <div className="mx-auto max-w-[1100px] px-5 py-8 sm:px-8 sm:py-10">
          <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.28em] text-brand-mute">
            <Link to="/dashboard" className="inline-flex items-center gap-2 hover:text-brand">
              <Home className="h-3 w-3" /> Dashboard
            </Link>
            <span className="text-brand-mute/40">/</span>
            <span className="text-brand">Checkout</span>
          </div>

          <h1 className="mt-6 font-serif-display text-[38px] font-medium leading-[0.98] tracking-tight sm:text-[46px] lg:text-[54px]">
            Confirm & <span className="italic text-brand">pay.</span>
          </h1>
          <p className="mt-3 max-w-[560px] text-[13.5px] text-brand-ivory/60">
            Secure Razorpay checkout. Nothing is charged until you press Pay. GST added at checkout.
          </p>

          {resumed && !error && (
            <div className="mt-6 flex items-start gap-3 rounded-md border border-brand-hair bg-brand/[0.05] px-4 py-3 text-[13px] text-brand-ivory/85">
              <RotateCcw className="mt-0.5 h-4 w-4 flex-none text-brand" />
              <div className="flex-1">
                <div className="font-serif-display text-[15px] leading-tight text-brand-ivory">
                  Welcome back. Your checkout was resumed.
                </div>
                <div className="mt-1 font-mono text-[10px] uppercase tracking-[0.28em] text-brand-mute">
                  Your details and cart are exactly where you left them.
                </div>
              </div>
              <button
                onClick={() => { clearAbandonedCheckout(); setResumed(false); }}
                className="font-mono text-[10px] uppercase tracking-[0.28em] text-brand-mute hover:text-brand"
              >
                Start over
              </button>
            </div>
          )}

          {error && (
            <div className="mt-6 flex items-start gap-3 rounded-md border border-brand/40 bg-brand/10 px-4 py-3 text-[13px] text-brand">
              <AlertTriangle className="mt-0.5 h-4 w-4 flex-none" />
              <div className="flex-1">{error}</div>
              <button
                onClick={pay}
                disabled={loading}
                className="rounded-md border border-brand/40 px-3 py-1 font-mono text-[10px] uppercase tracking-[0.24em] hover:bg-brand hover:text-white"
              >
                Retry
              </button>
            </div>
          )}

          <div className="mt-8 grid grid-cols-1 gap-8 lg:grid-cols-[1fr_420px]">
            {/* Customer + items */}
            <div className="space-y-6">
              <div className="rounded-xl border border-brand-hair bg-brand-panel p-6">
                <div className="font-mono text-[10px] uppercase tracking-[0.28em] text-brand-mute">
                  Billing details
                </div>
                <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2">
                  <Field label="Full name *">
                    <input
                      className="w-full rounded-md border border-brand-hair bg-brand-panel2 px-4 py-3 text-[13.5px] text-brand-ivory focus:border-brand"
                      value={customer.name}
                      onChange={(e) => update("name", e.target.value)}
                      placeholder="Your name"
                    />
                  </Field>
                  <Field label="Email *">
                    <input
                      type="email"
                      className="w-full rounded-md border border-brand-hair bg-brand-panel2 px-4 py-3 text-[13.5px] text-brand-ivory focus:border-brand"
                      value={customer.email}
                      onChange={(e) => update("email", e.target.value)}
                      placeholder="you@publisher.com"
                    />
                  </Field>
                  <Field label="Mobile *">
                    <input
                      type="tel"
                      className="w-full rounded-md border border-brand-hair bg-brand-panel2 px-4 py-3 text-[13.5px] text-brand-ivory focus:border-brand"
                      value={customer.phone}
                      onChange={(e) => update("phone", e.target.value)}
                      placeholder="10-digit mobile"
                    />
                  </Field>
                  <Field label="GST number (optional)">
                    <input
                      className="w-full rounded-md border border-brand-hair bg-brand-panel2 px-4 py-3 font-mono text-[13px] uppercase tracking-wider text-brand-ivory focus:border-brand"
                      value={customer.gst}
                      onChange={(e) => update("gst", e.target.value.toUpperCase())}
                      placeholder="15-digit GSTIN e.g. 27ABCDE1234F1Z5"
                      maxLength={15}
                    />
                  </Field>
                </div>
              </div>

              <div className="overflow-hidden rounded-xl border border-brand-hair bg-brand-panel">
                <div className="flex items-center justify-between border-b border-brand-hair px-6 py-4 font-mono text-[10px] uppercase tracking-[0.28em] text-brand-mute">
                  <span>Order summary</span>
                  <span>{plan.length} item{plan.length !== 1 ? "s" : ""}</span>
                </div>
                {plan.length === 0 ? (
                  <div className="px-6 py-10 text-center">
                    <div className="font-serif-display text-[22px]">Your plan is empty.</div>
                    <button
                      onClick={() => navigate("/dashboard")}
                      className="mt-4 inline-flex items-center gap-2 rounded-md bg-brand px-4 py-2.5 font-mono text-[10.5px] uppercase tracking-[0.28em] text-white hover:bg-[#ff5c26]"
                    >
                      Browse services <ArrowRight className="h-3.5 w-3.5" />
                    </button>
                  </div>
                ) : (
                  <ul className="divide-y divide-brand-hair/60">
                    {plan.map((item) => (
                      <li key={item.key || item.code} className="grid grid-cols-[64px_1fr_auto] items-center gap-4 px-6 py-4">
                        <div className="font-mono text-[10.5px] tracking-[0.22em] text-brand">{item.code}</div>
                        <div className="min-w-0">
                          <div className="truncate font-serif-display text-[16px] leading-tight text-brand-ivory">{item.title}</div>
                          <div className="truncate font-mono text-[10.5px] uppercase tracking-[0.22em] text-brand-mute">
                            {item.service} · {item.billing}
                          </div>
                        </div>
                        <div className="font-mono text-[13px] text-brand-ivory">{item.price}</div>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>

            {/* Payment card */}
            <div className="sticky top-4 rounded-xl border border-brand-hair bg-brand-panel p-6">
              <div className="font-mono text-[10px] uppercase tracking-[0.28em] text-brand-mute">
                Payable now
              </div>
              <div className="mt-2 font-serif-display text-[34px] leading-none text-brand sm:text-[40px] lg:text-[46px]">
                {formatINR(totals.total)}
              </div>
              <div className="mt-2 font-mono text-[10.5px] uppercase tracking-[0.22em] text-brand-mute">
                Includes 18% GST
              </div>

              <div className="mt-5 space-y-2 rounded-md border border-brand-hair bg-brand-panel2 p-4 text-[13px]">
                <Row label="Subtotal" value={formatINR(totals.subtotal)} />
                <Row label="GST @ 18%" value={formatINR(totals.gst)} muted />
                <div className="my-2 h-px bg-brand-hair" />
                <Row label="Total" value={formatINR(totals.total)} strong />
              </div>

              <label className="mt-5 flex items-start gap-2 text-[12px] text-brand-ivory/70">
                <input
                  type="checkbox"
                  checked={agree}
                  onChange={(e) => setAgree(e.target.checked)}
                  className="mt-0.5 h-4 w-4 accent-[#ff4e15]"
                />
                <span>
                  I agree to AuthorOS's terms of service and privacy policy.
                </span>
              </label>

              <button
                onClick={pay}
                disabled={loading || plan.length === 0}
                className="group mt-5 flex w-full items-center justify-center gap-3 rounded-md bg-brand py-3.5 font-mono text-[12px] font-semibold uppercase tracking-[0.28em] text-white transition-colors hover:bg-[#ff5c26] disabled:cursor-not-allowed disabled:opacity-60"
              >
                <Lock className="h-4 w-4" />
                {loading ? "Processing..." : `Pay ${formatINR(totals.total)}`}
              </button>

              <div className="mt-4 flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.28em] text-brand-mute">
                <ShieldCheck className="h-3.5 w-3.5 text-brand" /> Razorpay secured
              </div>
            </div>
          </div>

          <StudioFooter />
        </div>
      </main>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <div className="mb-1.5 font-mono text-[10px] uppercase tracking-[0.28em] text-brand-mute">{label}</div>
      {children}
    </div>
  );
}

function Row({ label, value, muted, strong }) {
  return (
    <div className={`flex items-baseline justify-between ${muted ? "text-brand-ivory/55" : "text-brand-ivory/85"}`}>
      <div className="text-[12.5px]">{label}</div>
      <div className={`font-mono ${strong ? "text-[15px] text-brand" : "text-[12.5px]"}`}>{value}</div>
    </div>
  );
}
